import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";

import { createAnimationChannel } from "../../animation/animationChannel";
import { MatchBoardSurface } from "../../components/MatchBoardSurface/MatchBoardSurface";
import { cellAt, coordinate, type Coordinate, type GameState, type Player } from "../../game";
import type { MotionPreference } from "../../platform/settings/settingsStore";
import type { MatchActor, MatchTransitionPresentation } from "../match/controller";
import type { MatchRecordV1 } from "../match/persistence";
import type { MatchSetupSelection } from "../setup";
import { eventBadges, replayMatchRecord } from "./historyData";

import "./HistoryReplay.css";

export type HistoryReplayProps = Readonly<{
  record: MatchRecordV1;
  discMarkers: boolean;
  motion: MotionPreference;
  onBack: () => void;
  onContinue?: (game: GameState, selection: MatchSetupSelection) => void;
  backLabel?: string;
  initialStep?: number;
  highlightedStep?: number;
  onShare?: (step: number) => Promise<ShareResult>;
  sourceLabel?: string;
}>;

export type ShareResult = Readonly<{
  kind: "copied" | "shared" | "cancelled" | "error";
  url?: string;
  message?: string;
}>;

export function HistoryReplay({
  record,
  discMarkers,
  motion,
  onBack,
  onContinue,
  backLabel = "Back to history",
  initialStep = record.moves.length,
  highlightedStep,
  onShare,
  sourceLabel = "Saved match",
}: HistoryReplayProps) {
  const replay = useMemo(() => replayMatchRecord(record), [record]);
  const [channel] = useState(createAnimationChannel);
  const active = useSyncExternalStore(channel.subscribe, channel.getSnapshot, channel.getSnapshot);
  const title = useRef<HTMLHeadingElement>(null);
  const [step, setStep] = useState(() => Math.max(0, Math.min(record.moves.length, initialStep)));
  const [shareResult, setShareResult] = useState<ShareResult | null>(null);
  const viewFrom: Player = record.mode === "local" ? "black" : record.humanPlayer;
  const shown = active?.request.previousGame ?? stateAt(replay, step);
  const transition = step === 0 ? null : (replay.transitions[step - 1] ?? null);
  const move = step === 0 ? null : (replay.moves[step - 1] ?? null);

  useEffect(() => () => channel.port.cancel(), [channel]);
  useEffect(() => title.current?.focus(), []);

  const play = (direction: "forward" | "reverse"): void => {
    if (active !== null) return;
    const edge = direction === "forward" ? step : step - 1;
    const request = presentation(record, replay, edge, direction);
    if (request === null) return;
    setStep((current) => current + (direction === "forward" ? 1 : -1));
    void channel.port.play(request);
  };

  const jump = (next: number): void => {
    channel.port.cancel();
    setStep(Math.max(0, Math.min(record.moves.length, next)));
  };

  const destination = move?.kind === "normal" ? cellAt(stateAt(replay, step), move.to) : null;
  const stacked = destination?.kind === "field" && destination.stack.length > 1;
  const reviewExitedDisc = exitedDisc(record, replay, step);
  const reviewExitIsAbove =
    reviewExitedDisc !== null &&
    (viewFrom === "black" ? reviewExitedDisc.position.row : 4 - reviewExitedDisc.position.row) < 0;

  return (
    <section className="history-replay" aria-labelledby="history-replay-title">
      <div className="history-replay__layout">
        <div className="history-replay__rail">
          <header className="history-replay__header">
            <button type="button" onClick={onBack}>
              {backLabel}
            </button>
            <div>
              <p>{sourceLabel}</p>
              <h1 id="history-replay-title" ref={title} tabIndex={-1}>
                Replay
              </h1>
            </div>
          </header>
          <div className="history-replay__sidebar">
            <section className="history-replay__move" aria-live="polite">
              <h2 aria-label={`Move ${step} of ${record.moves.length}`}>
                {`Move ${step}`}
                <span>{` of ${record.moves.length}`}</span>
              </h2>
              <p className="history-replay__description">
                {move === null
                  ? "Starting position"
                  : describeMove(
                      stateAt(replay, step - 1).activePlayer,
                      move.from,
                      move.to,
                      move.kind,
                      viewFrom,
                    )}
              </p>
              {transition === null ? null : (
                <ul className="history-replay__badges" aria-label="Move events">
                  {stacked ? <li>Stacked</li> : null}
                  {eventBadges(transition.events)
                    .filter((badge) => badge !== "Move")
                    .map((badge) => (
                      <li key={badge}>{badge}</li>
                    ))}
                </ul>
              )}
              {highlightedStep === step ? (
                <p className="history-replay__highlight">Shared turn</p>
              ) : null}
            </section>
            <ReplayControls
              active={active !== null}
              canContinue={shown.outcome === null}
              moveCount={record.moves.length}
              step={step}
              onContinue={
                onContinue === undefined
                  ? undefined
                  : () => onContinue(stateAt(replay, step), continuationSelection(record))
              }
              onJump={jump}
              onPlay={play}
            />
            {onShare === undefined ? null : (
              <div className="history-replay__share">
                <button
                  type="button"
                  onClick={() => {
                    setShareResult(null);
                    void onShare(step).then(setShareResult);
                  }}
                >
                  Share this turn
                </button>
                <p aria-live="polite">
                  {shareResult?.kind === "copied"
                    ? "Link copied."
                    : shareResult?.kind === "shared"
                      ? "Share sheet opened."
                      : shareResult?.kind === "error"
                        ? (shareResult.message ?? "The link could not be shared.")
                        : ""}
                </p>
                {shareResult?.url === undefined ? null : (
                  <label>
                    Share link
                    <input
                      aria-label="Share link"
                      readOnly
                      value={shareResult.url}
                      onFocus={(event) => event.currentTarget.select()}
                    />
                  </label>
                )}
              </div>
            )}
          </div>
        </div>
        <MatchBoardSurface
          activeAnimation={active}
          animationFeedback={{ emit: () => undefined }}
          aria-label="Passo history replay board"
          disabled
          discMarkers={discMarkers}
          className={reviewExitIsAbove ? "history-replay__board--exit-above" : undefined}
          motion={motion}
          onAnimationFinish={channel.finish}
          phase="finished"
          reviewExitedDisc={reviewExitedDisc}
          state={shown}
          viewFrom={viewFrom}
        />
      </div>
    </section>
  );
}

function ReplayControls({
  active,
  canContinue,
  moveCount,
  step,
  onContinue,
  onJump,
  onPlay,
}: Readonly<{
  active: boolean;
  canContinue: boolean;
  moveCount: number;
  step: number;
  onContinue?: (() => void) | undefined;
  onJump: (step: number) => void;
  onPlay: (direction: "forward" | "reverse") => void;
}>) {
  return (
    <div className="history-replay__controls">
      <div aria-label="Replay controls" className="review-actions__controls" role="group">
        <button
          aria-label="Go to start"
          className="review-actions__icon-button"
          disabled={active || step === 0}
          type="button"
          onClick={() => onJump(0)}
        >
          <ReplayIcon kind="start" />
        </button>
        <button
          aria-label="Previous move"
          className="review-actions__icon-button"
          disabled={active || step === 0}
          type="button"
          onClick={() => onPlay("reverse")}
        >
          <ReplayIcon kind="previous" />
        </button>
        <button
          aria-label="Next move"
          className="review-actions__icon-button"
          disabled={active || step === moveCount}
          type="button"
          onClick={() => onPlay("forward")}
        >
          <ReplayIcon kind="next" />
        </button>
        <button
          aria-label="Go to end"
          className="review-actions__icon-button"
          disabled={active || step === moveCount}
          type="button"
          onClick={() => onJump(moveCount)}
        >
          <ReplayIcon kind="end" />
        </button>
      </div>
      <label>
        Move
        <input
          aria-label="Replay move"
          type="range"
          min={0}
          max={moveCount}
          value={step}
          disabled={active || moveCount === 0}
          onChange={(event) => onJump(Number(event.currentTarget.value))}
        />
      </label>
      {onContinue === undefined ? null : (
        <button
          className="history-replay__continue"
          disabled={active || !canContinue}
          title={
            canContinue
              ? "Start a new match from the visible position"
              : "This position has already ended"
          }
          type="button"
          onClick={onContinue}
        >
          Continue from here
        </button>
      )}
    </div>
  );
}

function continuationSelection(record: MatchRecordV1): MatchSetupSelection {
  return record.mode === "local"
    ? Object.freeze({ mode: "local", humanPlayer: "black", difficulty: "medium" })
    : Object.freeze({
        mode: "bot",
        humanPlayer: record.humanPlayer,
        difficulty: record.difficulty,
      });
}

function ReplayIcon({ kind }: Readonly<{ kind: "start" | "previous" | "next" | "end" }>) {
  const previous = kind === "start" || kind === "previous";
  const endpoint = kind === "start" || kind === "end";
  return (
    <svg
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.9"
      viewBox="0 0 20 20"
    >
      {endpoint ? (
        <line x1={previous ? "5" : "15"} x2={previous ? "5" : "15"} y1="4" y2="16" />
      ) : null}
      <path
        d={
          previous
            ? endpoint
              ? "M14 5 8 10l6 5"
              : "M13 5 7 10l6 5"
            : endpoint
              ? "m6 5 6 5-6 5"
              : "m7 5 6 5-6 5"
        }
      />
    </svg>
  );
}

function presentation(
  record: MatchRecordV1,
  replay: ReturnType<typeof replayMatchRecord>,
  edge: number,
  direction: "forward" | "reverse",
): MatchTransitionPresentation | null {
  const move = replay.moves[edge];
  const transition = replay.transitions[edge];
  const prior = replay.states[edge];
  const current = replay.states[edge + 1];
  if (!move || !transition || !prior || !current) return null;
  const actor: MatchActor =
    record.mode === "local"
      ? prior.activePlayer
      : prior.activePlayer === record.humanPlayer
        ? "human"
        : "bot";
  const forward: MatchTransitionPresentation = Object.freeze({
    actor,
    move,
    previousGame: prior,
    transition,
    authority: Object.freeze({ epoch: record.id, revision: edge + 1 }),
    botDecision: null,
    focusAfter: Object.freeze({ kind: "board-heading" }),
    playback: Object.freeze({ direction: "forward" }),
    timing: "review",
  });
  if (direction === "forward") return forward;
  return Object.freeze({
    actor,
    move,
    previousGame: current,
    transition: Object.freeze({ state: prior, events: Object.freeze([]) }),
    authority: Object.freeze({ epoch: record.id, revision: edge }),
    botDecision: null,
    focusAfter: Object.freeze({ kind: "board-heading" }),
    playback: Object.freeze({
      direction: "reverse",
      forward: Object.freeze({ previousGame: prior, move, transition }),
    }),
    timing: "review",
  });
}

function describeMove(
  player: Player,
  from: Coordinate,
  to: Coordinate,
  kind: "normal" | "passage",
  viewFrom: Player,
): string {
  return `${capitalize(player)} moved from ${positionName(from, viewFrom)} ${kind === "passage" ? "through the frontier" : `to ${positionName(to, viewFrom)}`}.`;
}
function positionName(position: Coordinate, viewFrom: Player): string {
  const row = viewFrom === "black" ? position.row : 4 - position.row;
  const column = viewFrom === "black" ? position.column : 4 - position.column;
  const rowName =
    ["far row", "second row from far edge", "middle row", "second row from near edge", "near row"][
      row
    ] ?? "beyond the board";
  return `${rowName}, column ${column + 1} from left`;
}
function exitedDisc(
  record: MatchRecordV1,
  replay: ReturnType<typeof replayMatchRecord>,
  step: number,
) {
  if (step !== record.moves.length || record.result.kind !== "passage") return null;
  const event = replay.transitions.at(-1)?.events.find((item) => item.kind === "disc-exited");
  return event?.kind === "disc-exited"
    ? { player: event.player, position: coordinate(event.to.row, event.to.column) }
    : null;
}
function stateAt(replay: ReturnType<typeof replayMatchRecord>, step: number) {
  const state = replay.states[step];
  if (state === undefined) throw new RangeError(`Replay state ${step} does not exist.`);
  return state;
}
function capitalize(value: string): string {
  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
}
